import { useEffect, useState } from "react";
import { Container, Tab, Tabs, Spinner, Form, Button, InputGroup } from "react-bootstrap";
import { useParams } from "react-router-dom";
import ImageGallery from "../ImageGallery/ImageGallery";
import useFetch from "../../../hooks/useFetch";

interface ImageItem {
  url: string;
  nombre: string;
}

const ImageManager = () => {
  const { idActividad } = useParams<{ idActividad: string }>();
  const [actividadImages, setActividadImages] = useState<ImageItem[]>([]);
  const [verificacionImages, setVerificacionImages] = useState<ImageItem[]>([]);
  const [manipulacionImages, setManipulacionImages] = useState<ImageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [descripcionServicio, setDescripcionServicio] = useState("");
  const [descripcionOriginal, setDescripcionOriginal] = useState("");
  const [savingDescripcion, setSavingDescripcion] = useState(false);
  const [descripcionMsg, setDescripcionMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const { sendRequest } = useFetch();

  const loadImages = () => {
    setLoading(true);
    sendRequest(
      { url: `/foto/${idActividad}` },
      (data: any) => {
        setActividadImages(data.actividad || []);
        setVerificacionImages(data.verificacion || []);
        setManipulacionImages(data.manipulacion || []);
        setLoading(false);
      }
    );
  };

  const loadDescripcion = () => {
    sendRequest(
      { url: `/actividad/${idActividad}` },
      (data: any) => {
        const desc = data?.data?.descripcion_servicio || "";
        setDescripcionServicio(desc);
        setDescripcionOriginal(desc);
      }
    );
  };

  const handleSaveDescripcion = () => {
    setSavingDescripcion(true);
    setDescripcionMsg(null);
    sendRequest(
      {
        url: `/actividad/${idActividad}/descripcion`,
        method: "PATCH",
        body: { descripcion_servicio: descripcionServicio },
      },
      (data: any) => {
        setSavingDescripcion(false);
        if (data?.success) {
          setDescripcionOriginal(descripcionServicio);
          setDescripcionMsg({ type: "success", text: "Descripción guardada correctamente." });
        } else {
          setDescripcionMsg({ type: "error", text: "Error al guardar la descripción." });
        }
      }
    );
  };

  useEffect(() => {
    if (idActividad) {
      loadImages();
      loadDescripcion();
    }
  }, [idActividad]);

  if (loading) {
    return (
      <Container className="text-center" style={{ paddingTop: "2rem" }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  const descripcionModificada = descripcionServicio !== descripcionOriginal;

  return (
    <Container style={{ paddingTop: "2rem", paddingBottom: "2rem" }}>
      <h2>Gestión de Imágenes - Actividad {idActividad}</h2>

      <Form.Group className="mb-4">
        <Form.Label><strong>Descripción del Servicio</strong></Form.Label>
        <InputGroup>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Descripción del servicio..."
            value={descripcionServicio}
            onChange={(e) => {
              setDescripcionServicio(e.target.value);
              setDescripcionMsg(null);
            }}
          />
        </InputGroup>
        <div className="d-flex align-items-center mt-2 gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSaveDescripcion}
            disabled={!descripcionModificada || savingDescripcion}
          >
            {savingDescripcion ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Guardar descripción"
            )}
          </Button>
          {descripcionMsg && (
            <span
              className={
                descripcionMsg.type === "success" ? "text-success" : "text-danger"
              }
              style={{ fontSize: "0.875rem" }}
            >
              {descripcionMsg.text}
            </span>
          )}
        </div>
      </Form.Group>

      <Tabs defaultActiveKey="actividad" className="mb-3">
        <Tab eventKey="actividad" title="Fotos de Servicio">
          <ImageGallery
            idActividad={Number(idActividad)}
            images={actividadImages}
            tipo="actividad"
            onImageDeleted={loadImages}
          />
        </Tab>
        
        <Tab eventKey="verificacion" title="Fotos de Verificación">
          <ImageGallery
            idActividad={Number(idActividad)}
            images={verificacionImages}
            tipo="verificacion"
            onImageDeleted={loadImages}
          />
        </Tab>
        
        <Tab eventKey="manipulacion" title="Fotos de Manipulación">
          <ImageGallery
            idActividad={Number(idActividad)}
            images={manipulacionImages}
            tipo="manipulacion"
            onImageDeleted={loadImages}
          />
        </Tab>
      </Tabs>
    </Container>
  );
};

export default ImageManager;
